<template>
  <div>
    <h4 class="mb-1" style="font-size: 22px; font-weight: 600;">Розрахунок вартості НВ</h4>
    <p class="text-muted mb-4" style="font-size: 13px;">Панель управління / НВ</p>

    <div class="accident-calc-grid">
      <!-- 1. Основна форма: Термін дії, Дата народження, Територія, Страхова сума -->
      <div class="accident-grid-form">
        <div class="row">
          <!-- Ліва колонка: Термін дії -->
          <div class="col-md-6 mb-4">
            <div class="accident-card h-100">
              <h5 class="accident-card__title">Термін дії</h5>

              <div class="form-group">
                <label class="form-title">Дата початку дії договору</label>
                <date-picker
                  v-date-mask
                  name="start_date"
                  v-model="start_date"
                  :config="optionsDate"
                  :class="['date-input__dp', {invalid: ((!$v.start_date.required || !$v.start_date.validDate) && submitCheck)}]"
                  placeholder="ДД.ММ.РРРР">
                </date-picker>
                <small class="error" v-if="!$v.start_date.required && submitCheck">
                  Вкажіть дату
                </small>
                <small class="error" v-else-if="!$v.start_date.validDate && submitCheck">
                  Вкажіть коректну дату
                </small>
              </div>

              <div class="form-group">
                <label class="form-title">Період дії договору</label>
                <multiselect
                  :class="['form-control accident-search-select', {invalid: ((!$v.periodOption.required) && submitCheck)}]"
                  v-model="periodOption"
                  :options="periods"
                  placeholder="Оберіть період"
                  :allow-empty="false"
                  label="name"
                  track-by="value"
                >
                </multiselect>
                <small class="error" v-if="!$v.periodOption.required && submitCheck">
                  Вкажіть період
                </small>
              </div>

              <div class="form-group mb-0">
                <label class="form-title">Період дії страхового захисту</label>
                <multiselect
                  :class="['form-control accident-search-select', {invalid: ((!$v.protectionPeriod.required) && submitCheck)}]"
                  v-model="protectionPeriod"
                  :options="protectionPeriods"
                  placeholder="Оберіть період захисту"
                  :allow-empty="false"
                  label="name"
                  track-by="value"
                >
                </multiselect>
                <small class="error" v-if="!$v.protectionPeriod.required && submitCheck">
                  Вкажіть період захисту
                </small>
              </div>
            </div>
          </div>

          <!-- Права колонка в лівій частині: Дата народження та Територія -->
          <div class="col-md-6 mb-4">
            <div class="accident-card mb-3">
              <h5 class="accident-card__title">Дата народження застрахованої особи</h5>

              <div class="form-group mb-0">
                <label class="form-title">Дата народження</label>
                <date-picker
                  v-date-mask
                  name="birth_date"
                  v-model="birthDate"
                  :config="optionsBirthDate"
                  :class="['date-input__dp', {invalid: ((!$v.birthDate.required || !$v.birthDate.validDate || !$v.birthDate.minAge) && submitCheck)}]"
                  placeholder="ДД.ММ.РРРР">
                </date-picker>
                <small class="error" v-if="!$v.birthDate.required && submitCheck">
                  Вкажіть дату народження
                </small>
                <small class="error" v-else-if="!$v.birthDate.validDate && submitCheck">
                  Вкажіть коректну дату народження
                </small>
                <small class="error" v-else-if="!$v.birthDate.minAge && submitCheck">
                  Вік застрахованої особи повинен бути від 1 року
                </small>
              </div>
            </div>

            <div class="accident-card">
              <h5 class="accident-card__title">Територія страхування</h5>

              <div class="form-group mb-0">
                <label class="form-title">Територія страхування</label>
                <multiselect
                  :class="['form-control accident-search-select', {invalid: ((!$v.country.required) && submitCheck)}]"
                  v-model="country"
                  :options="countries"
                  placeholder="Оберіть територію"
                  :allow-empty="false"
                  label="name"
                  track-by="id"
                >
                </multiselect>
                <small class="error" v-if="!$v.country.required && submitCheck">
                  Вкажіть територію
                </small>
              </div>
            </div>
          </div>

          <!-- Загальна страхова сума -->
          <div class="col-12 mb-4">
            <div class="accident-card">
              <h5 class="accident-card__title">Загальна страхова сума</h5>

              <div class="form-group mb-0">
                <label class="form-title">Страхова сума</label>
                <multiselect
                  :class="['form-control accident-search-select', {invalid: ((!$v.sum.required) && submitCheck)}]"
                  v-model="sum"
                  :options="sumOptions"
                  placeholder="Вкажіть суму"
                  :allow-empty="false"
                  label="name"
                  track-by="value"
                >
                </multiselect>
                <small class="error" v-if="!$v.sum.required && submitCheck">
                  Вкажіть суму
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 2. Фактори що впливають на фінальну вартість (на десктопі праворуч, на мобілці між формою та ризиками) -->
      <div class="accident-grid-factors mb-4">
        <div class="accident-card">
          <h5 class="accident-card__title">Фактори що впливають на фінальну вартість</h5>

          <div class="accident-factors-checkboxes mb-3">
            <label class="accident-check-label" :class="{ active: hasWarRisk }">
              <input type="checkbox" v-model="hasWarRisk" class="accident-check-input">
              <span class="accident-check-box"><i v-if="hasWarRisk" class="fa fa-check"></i></span>
              Воєнні ризики
            </label>
            <label class="accident-check-label" :class="{ active: hasSport }">
              <input type="checkbox" v-model="hasSport" class="accident-check-input">
              <span class="accident-check-box"><i v-if="hasSport" class="fa fa-check"></i></span>
              Спорт
            </label>
            <label v-if="!isChild" class="accident-check-label" :class="{ active: hasProfession }">
              <input type="checkbox" v-model="hasProfession" class="accident-check-input">
              <span class="accident-check-box"><i v-if="hasProfession" class="fa fa-check"></i></span>
              Професія
            </label>
          </div>

          <!-- Спорт -->
          <div class="accident-factor-block" v-if="hasSport">
            <h6 class="accident-factor-block__title">Спорт</h6>

            <!-- Перемикач режиму: Один вид спорту або Група спорту -->
            <div class="sport-mode-toggle mb-3">
              <label class="sport-mode-option" :class="{ active: !isSportGroup }">
                <input type="radio" :value="false" v-model="isSportGroup" class="sport-mode-radio">
                <span class="sport-mode-text">Один вид спорту</span>
              </label>
              <label class="sport-mode-option" :class="{ active: isSportGroup }">
                <input type="radio" :value="true" v-model="isSportGroup" class="sport-mode-radio">
                <span class="sport-mode-text">Група спорту</span>
              </label>
            </div>

            <!-- Варіант 1: Один вид спорту -->
            <template v-if="!isSportGroup">
              <div class="form-group">
                <label class="form-title">Вид спорту</label>
                <multiselect
                  :class="['form-control accident-search-select', {invalid: ((!$v.sportType.required) && submitCheck)}]"
                  v-model="sportType"
                  :options="sportTypes"
                  placeholder="Оберіть вид спорту"
                  :allow-empty="false"
                  label="name"
                  track-by="value"
                >
                </multiselect>
                <small class="error" v-if="!$v.sportType.required && submitCheck">
                  Оберіть вид спорту
                </small>
              </div>
            </template>

            <!-- Варіант 2: Група спорту (для кількох видів) -->
            <template v-else>
              <div class="form-group">
                <label class="form-title">Група спорту (для кількох видів)</label>
                <multiselect
                  :class="['form-control accident-search-select', {invalid: ((!$v.sportGroup.required) && submitCheck)}]"
                  v-model="sportGroup"
                  :options="sportGroups"
                  placeholder="Оберіть групу спорту"
                  :allow-empty="false"
                  label="name"
                  track-by="code"
                >
                </multiselect>
                <small class="error" v-if="!$v.sportGroup.required && submitCheck">
                  Оберіть групу спорту
                </small>
              </div>

              <!-- Перегляд видів спорту в обраній групі -->
              <div v-if="sportGroup" class="sport-group-details p-3 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="font-weight-bold" style="font-size: 13px; color: #2d3748;">
                    Види спорту в групі ({{ selectedGroupSports.length }}):
                  </span>
                  <button type="button" class="btn btn-sm btn-link p-0 text-primary" style="font-size: 12px; font-weight: 500; text-decoration: none;" @click="toggleSportsList">
                    {{ showSportsList ? 'Згорнути' : 'Переглянути список' }}
                  </button>
                </div>
                <div class="text-muted small mb-2" style="font-size: 11px; line-height: 1.4;">
                  {{ sportGroup.description }}
                </div>

                <div v-if="showSportsList" class="mt-2">
                  <input
                    type="text"
                    v-model="sportsSearchQuery"
                    class="form-control form-control-sm mb-2"
                    placeholder="Пошук виду спорту в цій групі..."
                    style="font-size: 12px; height: 32px;"
                  />
                  <div class="sport-group-sports-scroll">
                    <ul class="list-unstyled mb-0" style="font-size: 12px;">
                      <li v-for="sName in filteredSelectedGroupSports" :key="sName" class="sport-list-item py-1">
                        <i class="fa fa-check text-success mr-1" style="font-size: 11px;"></i> {{ sName }}
                      </li>
                      <li v-if="filteredSelectedGroupSports.length === 0" class="text-muted py-2 text-center" style="font-size: 12px;">
                        Нічого не знайдено за запитом "{{ sportsSearchQuery }}"
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </template>

            <!-- Категорія спорту -->
            <div class="form-group mb-0">
              <label class="form-title">Категорія спорту</label>
              <multiselect
                :class="['form-control accident-search-select', {invalid: ((!$v.sportCategory.required) && submitCheck)}]"
                v-model="sportCategory"
                :options="filteredSportCategories"
                placeholder="Оберіть категорію"
                :allow-empty="false"
                label="name"
                track-by="value"
              >
              </multiselect>
              <small class="error" v-if="!$v.sportCategory.required && submitCheck">
                Оберіть категорію
              </small>
            </div>
          </div>

          <!-- Професія -->
          <div class="accident-factor-block" v-if="!isChild && hasProfession">
            <h6 class="accident-factor-block__title">Професія</h6>
            <div class="form-group mb-0">
              <label class="form-title">Назва професії</label>
              <multiselect
                :class="['form-control accident-search-select', {invalid: ((!$v.profession.required) && submitCheck)}]"
                v-model="profession"
                :options="professions"
                placeholder="Оберіть професію"
                :allow-empty="false"
                label="name"
                track-by="value"
              >
              </multiselect>
              <small class="error" v-if="!$v.profession.required && submitCheck">
                Оберіть професію
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- 3. Пакет ризиків -->
      <div class="accident-grid-risks mb-4">
        <div class="accident-card accident-package-card">
          <h5 class="accident-package-card__title">Пакет ризиків</h5>

          <!-- Дитяча програма (якщо вік < 18 років) або вибір пакета для дорослих -->
          <div v-if="isChild" class="accident-child-program-badge mb-3">
            <div class="accident-child-program-badge__icon">
              <i class="fa fa-child"></i>
            </div>
            <div class="accident-child-program-badge__content">
              <div class="accident-child-program-badge__title">Дитяча спортивна програма</div>
              <div class="accident-child-program-badge__desc">Спеціальні умови та ризики для дітей віком від 1 до 17 років</div>
            </div>
          </div>

          <div v-else class="accident-package-tabs mb-3">
            <button
              v-for="pkg in packages"
              :key="pkg.id"
              type="button"
              class="accident-package-tab"
              :class="{ active: selectedPackage === pkg.id }"
              @click="selectPackage(pkg.id)"
            >
              {{ pkg.name }}
            </button>
          </div>

          <!-- Рядок лічильника -->
          <div class="accident-package-counter mb-2">
            {{ counterText }}
          </div>

          <!-- Список ризиків обраного пакета -->
          <div class="accident-package-risks-list">
            <div
              v-for="risk in currentPackageRisks"
              :key="risk.key"
              class="accident-package-risk-row"
              :class="{
                'is-mandatory': risk.mandatory,
                'is-active': isRiskActive(risk.key)
              }"
              @click="toggleRisk(risk.key, risk.mandatory)"
            >
              <div
                class="accident-package-checkbox"
                :class="{
                  'is-checked': isRiskActive(risk.key),
                  'is-locked': risk.mandatory
                }"
              >
                <svg
                  v-if="isRiskActive(risk.key)"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <div class="accident-package-risk-body">
                <div class="accident-package-risk-name">
                  {{ risk.name }}
                </div>
                <div v-if="risk.description" class="accident-package-risk-desc">
                  {{ risk.description }}
                </div>
              </div>
              <div v-if="risk.mandatory" class="accident-package-risk-lock ml-auto">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#94a3b8"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </div>
            </div>
          </div>

          <!-- Блок додаткових ризиків (доклацати інші) -->
          <div v-if="enableExtraRisks && currentExtraRisks.length > 0" class="accident-extra-risks-section mt-3 pt-2">
            <button
              type="button"
              class="btn-toggle-extra-risks"
              @click="showExtraRisks = !showExtraRisks"
            >
              <span>
                <i :class="showExtraRisks ? 'fa fa-chevron-up mr-1' : 'fa fa-plus-circle mr-1'"></i>
                {{ showExtraRisks ? 'Приховати додаткові ризики' : '+ Додати інші ризики' }}
              </span>
              <span v-if="extraActiveCount > 0" class="badge badge-pill badge-primary ml-2">
                обрано +{{ extraActiveCount }}
              </span>
            </button>

            <div v-show="showExtraRisks" class="accident-extra-risks-list mt-2">
              <div
                v-for="risk in currentExtraRisks"
                :key="risk.key"
                class="accident-package-risk-row extra-risk"
                :class="{ 'is-active': isRiskActive(risk.key) }"
                @click="toggleRisk(risk.key, false)"
              >
                <div
                  class="accident-package-checkbox"
                  :class="{ 'is-checked': isRiskActive(risk.key) }"
                >
                  <svg
                    v-if="isRiskActive(risk.key)"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div class="accident-package-risk-body">
                  <div class="accident-package-risk-name">
                    {{ risk.name }}
                  </div>
                  <div v-if="risk.description" class="accident-package-risk-desc">
                    {{ risk.description }}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- 4. Нижній блок -->
      <div class="accident-grid-submit mb-4">
        <p class="text-muted mb-3" style="font-size: 13px;">
          Заповніть всі поля (*) щоб мати можливість скористатися пошуком
        </p>
        <button type="button" class="btn btn-blue accident-submit-btn"
                @click.prevent="setSearchParams">Показати результати
        </button>
      </div>
    </div>


  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import {required, requiredIf} from 'vuelidate/lib/validators';
import {
  rawApiData,
  parseSportsList,
  sportCategories,
  sportGroupMap,
  SPORT_GROUPS_LIST,
  resolveSportGroupCode
} from '@/views/accident/data/sportTypes';
import {
  ACCIDENT_PACKAGES,
  CHILD_PACKAGE,
  getPackageRisks,
  getExtraRisks,
  getDefaultAccidentRisks,
  detectPackageFromRisks,
  buildAccidentRisksPayload
} from '@/views/accident/data/accidentRisks';
import { ACCIDENT_PERIODS, normalizeAccidentPeriod } from '@/views/accident/data/accidentPeriods';
import { ACCIDENT_PROFESSIONS, legacyProfessionMap } from '@/views/accident/data/professions';
import moment from "moment";
import dateMask from '@/directives/dateMask';

export default {
  directives: { dateMask },
  components: {Multiselect},
  props: [
    'dataStartDate',
    'dataPeriodOption',
    'dataProtectionPeriod',
    'dataBirthDate',
    'dataCountry',
    'dataSum',
    'dataActiveFactor',
    'dataWarRisk',
    'dataSportType',
    'dataSportCategory',
    'dataProfession',
    'dataIsSportGroup',
    'dataSportGroup',
    'dataAccidentRisks'
  ],
  data: function () {
    const defaultPeriod = ACCIDENT_PERIODS.find(o => o.value === 365) || ACCIDENT_PERIODS[ACCIDENT_PERIODS.length - 1];
    let initialPeriod = defaultPeriod;
    if (this.dataPeriodOption) {
      const pVal = typeof this.dataPeriodOption === 'object' ? this.dataPeriodOption.value : this.dataPeriodOption;
      initialPeriod = ACCIDENT_PERIODS.find(o => o.value === normalizeAccidentPeriod(pVal)) || defaultPeriod;
    }

    let initIsSportGroup = !!this.dataIsSportGroup;
    let initSportGroup = null;
    if (this.dataSportGroup) {
      initIsSportGroup = true;
      initSportGroup = SPORT_GROUPS_LIST.find(g => g.code === this.dataSportGroup) || null;
    } else if (this.dataSportType) {
      const codeStr = typeof this.dataSportType === 'object' ? this.dataSportType.value : String(this.dataSportType);
      const isTechGroup = ['1409218', '1409219', '1409221', '1409220', '1409222', '1409223', '1409224', '1409225'].includes(codeStr);
      if (isTechGroup) {
        initIsSportGroup = true;
        const gKey = sportGroupMap[codeStr];
        initSportGroup = SPORT_GROUPS_LIST.find(g => g.code === gKey) || null;
      }
    }

    let initIsChild = false;
    if (this.dataBirthDate && this.dataBirthDate.length === 10) {
      const m = moment(this.dataBirthDate, 'DD.MM.YYYY', true);
      if (m.isValid()) {
        initIsChild = (moment().diff(m, 'years') < 18);
      }
    }

    let initPackage = initIsChild ? 'child' : 'adults';
    if (!initIsChild && this.dataAccidentRisks && typeof this.dataAccidentRisks === 'object') {
      initPackage = detectPackageFromRisks(this.dataAccidentRisks, false);
    }
    let initRisks = getDefaultAccidentRisks(initPackage, initIsChild);
    if (this.dataAccidentRisks && typeof this.dataAccidentRisks === 'object') {
      const parsed = {};
      Object.keys(this.dataAccidentRisks).forEach(k => {
        const v = this.dataAccidentRisks[k];
        if (typeof v === 'boolean') {
          parsed[k] = v;
        } else if (typeof v === 'string') {
          parsed[k] = (v === 'Y' || v === '1' || v.toLowerCase() === 'true');
        }
      });
      initRisks = { ...initRisks, ...parsed };
    }
    initRisks.RiskDeathAccident = true;

    return {
      packages: ACCIDENT_PACKAGES,
      selectedPackage: initPackage,
      showExtraRisks: false,
      risks: initRisks,
      periods: ACCIDENT_PERIODS,
      protectionPeriods: [
        { name: '24/7', value: '24_7' },
        { name: 'Під час учбових занять / службових обов’язків', value: 'study_work_hours' },
        { name: 'Під час занять спортом/змагань', value: 'sport_hours' },
      ],
      sumOptions: [
        { name: '10 000', value: 10000 },
        { name: '15 000', value: 15000 },
        { name: '25 000', value: 25000 },
        { name: '50 000', value: 50000 },
        { name: '100 000', value: 100000 },
        { name: '150 000', value: 150000 },
        { name: '200 000', value: 200000 },
        { name: '250 000', value: 250000 },
        { name: '300 000', value: 300000 },
      ],
      countries: [
        { name: 'Україна', id: 1 },
        { name: 'Весь світ', id: 2 },
      ],
      sportTypes: parseSportsList(rawApiData),
      sportCategories: sportCategories,
      sportGroups: SPORT_GROUPS_LIST,
      isSportGroup: initIsSportGroup,
      sportGroup: initSportGroup,
      showSportsList: false,
      enableExtraRisks: false,
      sportsSearchQuery: '',
      professions: ACCIDENT_PROFESSIONS,
      sum: this.dataSum || { name: '10 000', value: 10000 },
      periodOption: initialPeriod,
      protectionPeriod: this.dataProtectionPeriod || { name: '24/7', value: '24_7' },
      country: this.dataCountry || { name: 'Україна', id: 1 },
      birthDate: this.dataBirthDate || '',
      hasWarRisk: this.dataWarRisk || false,
      hasSport: !!this.dataSportType || initIsSportGroup,
      hasProfession: !initIsChild && !!this.dataProfession,
      sportType: this.dataSportType || null,
      sportCategory: this.dataSportCategory || null,
      profession: (!initIsChild && this.dataProfession)
        ? (ACCIDENT_PROFESSIONS.find(o => o.value === (legacyProfessionMap[String(this.dataProfession)] || String(this.dataProfession))) || null)
        : null,
      submitCheck: false,
      start_date: this.dataStartDate || moment().add(1, 'day').format('DD.MM.YYYY'),
      optionsDate: {
        format: 'DD.MM.YYYY',
        useCurrent: false,
        minDate: new Date(new Date().getFullYear(), new Date().getMonth(), (new Date().getDate() + 1)),
        locale: 'uk',
        icons: {
          previous: 'icon arrow-point-to-left',
          next: 'icon arrow-point-to-right',
        },
      },
      optionsBirthDate: {
        format: 'DD.MM.YYYY',
        useCurrent: false,
        minDate: new Date(new Date().getFullYear() - 100, new Date().getMonth(), new Date().getDate()),
        maxDate: new Date(new Date().getFullYear() - 1, new Date().getMonth(), new Date().getDate()),
        defaultDate: new Date(new Date().getFullYear() - 35, new Date().getMonth(), new Date().getDate()),
        locale: 'uk',
        icons: {
          previous: 'icon arrow-point-to-left',
          next: 'icon arrow-point-to-right',
        },
      },
    };
  },
  computed: {
    isChild() {
      if (!this.birthDate || this.birthDate.length !== 10) return false;
      const m = moment(this.birthDate, 'DD.MM.YYYY', true);
      if (!m.isValid()) return false;
      const age = moment().diff(m, 'years');
      return age >= 0 && age < 18;
    },
    filteredSportCategories() {
      return this.sportCategories;
    },
    selectedGroupSports() {
      if (!this.sportGroup || !this.sportGroup.sports) return [];
      return this.sportGroup.sports;
    },
    filteredSelectedGroupSports() {
      const sports = this.selectedGroupSports;
      if (!this.sportsSearchQuery) return sports;
      const q = this.sportsSearchQuery.toLowerCase().trim();
      return sports.filter(s => s.toLowerCase().includes(q));
    },
    currentPackageRisks() {
      return getPackageRisks(this.selectedPackage, this.isChild);
    },
    currentExtraRisks() {
      return getExtraRisks(this.selectedPackage, this.isChild);
    },
    extraActiveCount() {
      return this.currentExtraRisks.filter(r => this.isRiskActive(r.key)).length;
    },
    counterText() {
      const pkgRisks = this.currentPackageRisks;
      const pkgActiveCount = pkgRisks.filter(r => this.isRiskActive(r.key)).length;
      if (this.showExtraRisks) {
        const totalVisible = pkgRisks.length + this.currentExtraRisks.length;
        const totalActive = pkgActiveCount + this.extraActiveCount;
        return `Ризики · обрано ${totalActive} з ${totalVisible}`;
      }
      if (this.extraActiveCount > 0) {
        const totalVisible = pkgRisks.length + this.extraActiveCount;
        const totalActive = pkgActiveCount + this.extraActiveCount;
        return `Ризики · обрано ${totalActive} з ${totalVisible}`;
      }
      return `Ризики · обрано ${pkgActiveCount} з ${pkgRisks.length}`;
    },
  },
  validations: {
    sum: { required },
    periodOption: { required },
    protectionPeriod: { required },
    start_date: {
      required,
      validDate(val) {
        if (!val) return true;
        if (val.length !== 10) return false;
        const m = moment(val, 'DD.MM.YYYY', true);
        return m.isValid() && m.isSameOrAfter(moment().startOf('day'));
      },
    },
    birthDate: {
      required,
      validDate(val) {
        if (!val) return true;
        if (val.length !== 10) return false;
        const m = moment(val, 'DD.MM.YYYY', true);
        return m.isValid() && m.isBefore(moment()) && m.isAfter(moment().subtract(120, 'years'));
      },
      minAge(val) {
        if (!val || val.length !== 10) return true;
        const m = moment(val, 'DD.MM.YYYY', true);
        if (!m.isValid()) return true;
        return moment().diff(m, 'years') >= 1;
      },
    },
    country: { required },
    sportType: {
      required: requiredIf(function () {
        return this.hasSport && !this.isSportGroup;
      }),
    },
    sportGroup: {
      required: requiredIf(function () {
        return this.hasSport && this.isSportGroup;
      }),
    },
    sportCategory: {
      required: requiredIf(function () {
        return this.hasSport;
      }),
    },
    profession: {
      required: requiredIf(function () {
        return !this.isChild && this.hasProfession;
      }),
    },
  },
  watch: {
    isChild(val) {
      if (val) {
        this.hasProfession = false;
        this.profession = null;
      }
    },
    birthDate(val) {
      if (val && val.length === 10) {
        const m = moment(val, 'DD.MM.YYYY', true);
        if (m.isValid()) {
          const age = moment().diff(m, 'years');
          if (age < 18) {
            if (this.selectedPackage !== 'child') {
              this.selectedPackage = 'child';
              this.risks = getDefaultAccidentRisks('child', true);
            }
            this.hasProfession = false;
            this.profession = null;
          } else {
            if (this.selectedPackage === 'child') {
              this.selectedPackage = 'adults';
              this.risks = getDefaultAccidentRisks('adults', false);
            }
          }
        }
      }
    },
    hasSport(val) {
      if (!val) {
        this.sportType = null;
        this.sportGroup = null;
        this.sportCategory = null;
        this.showSportsList = false;
        this.sportsSearchQuery = '';
      } else {
        if (!this.sportCategory) {
          this.sportCategory = this.sportCategories[0] || null;
        }
        if (this.isSportGroup && !this.sportGroup) {
          this.sportGroup = this.sportGroups[0];
        }
      }
    },
    isSportGroup(isGroup) {
      this.showSportsList = false;
      this.sportsSearchQuery = '';
      if (isGroup) {
        if (!this.sportGroup) {
          const gKey = this.sportType ? this.sportType.group : 'S1';
          this.sportGroup = this.sportGroups.find(g => g.code === gKey) || this.sportGroups[1] || this.sportGroups[0];
        }
      }
    },
    sportGroup(newGroup) {
      this.sportsSearchQuery = '';
      if (newGroup && !this.sportCategory) {
        this.sportCategory = this.sportCategories[0] || null;
      }
    },
    hasProfession(val) {
      if (!val) {
        this.profession = null;
      }
    },
    sportType(newSport) {
      if (newSport) {
        if (!this.sportCategory) {
          this.sportCategory = this.sportCategories[0] || null;
        }
      } else if (!this.isSportGroup) {
        this.sportCategory = null;
      }
    },
  },
  mounted: function () {
    if (this.$router.currentRoute.name === 'accidentList') {
      const searchInfo = this.$store.getters.GET_SEARCH_ACCIDENT_PARAMS;
      if (searchInfo) {
        this.sum = this.sumOptions.find(o => o.value === searchInfo.sum) || null;
        this.start_date = searchInfo.startDate;
        const normalizedPeriod = normalizeAccidentPeriod(searchInfo.period);
        this.periodOption = this.periods.find(o => o.value === normalizedPeriod) || null;
        this.protectionPeriod = this.protectionPeriods.find(o => o.value === searchInfo.protectionPeriod) || null;
        this.birthDate = searchInfo.birthDate;
        this.country = this.countries.find(o => o.id === searchInfo.countryId) || null;
        this.hasWarRisk = searchInfo.warRisk || (Array.isArray(searchInfo.activeFactors) && searchInfo.activeFactors.includes('warRisk')) || searchInfo.activeFactor === 'warRisk';
        this.hasSport = !!searchInfo.sportType || !!searchInfo.sportGroup || (Array.isArray(searchInfo.activeFactors) && searchInfo.activeFactors.includes('sport')) || searchInfo.activeFactor === 'sport';
        this.hasProfession = !!searchInfo.profession || (Array.isArray(searchInfo.activeFactors) && searchInfo.activeFactors.includes('profession')) || searchInfo.activeFactor === 'profession';

        const isGroupParam = searchInfo.isSportGroup || !!searchInfo.sportGroup;
        const isTechGroupCode = ['1409218', '1409219', '1409221', '1409220', '1409222', '1409223', '1409224', '1409225'].includes(String(searchInfo.sportType));

        if (isGroupParam || isTechGroupCode) {
          this.isSportGroup = true;
          const groupCode = searchInfo.sportGroup || sportGroupMap[String(searchInfo.sportType)] || 'S1';
          this.sportGroup = this.sportGroups.find(g => g.code === groupCode) || this.sportGroups[0];
          this.sportType = null;
        } else if (searchInfo.sportType) {
          this.isSportGroup = false;
          this.sportType = this.sportTypes.find(o => o.value === String(searchInfo.sportType)) || null;
        }

        this.sportCategory = searchInfo.sportCategory
          ? this.sportCategories.find(o => o.value === searchInfo.sportCategory) || this.sportCategories[0] || null
          : (this.hasSport ? this.sportCategories[0] : null);
        const savedRisks = searchInfo.accidentRisks || searchInfo.accident;
        const isSavedChild = (this.birthDate && this.birthDate.length === 10)
          ? (moment().diff(moment(this.birthDate, 'DD.MM.YYYY', true), 'years') < 18)
          : false;

        if (isSavedChild) {
          this.hasProfession = false;
          this.profession = null;
        } else if (searchInfo.profession) {
          const pCode = legacyProfessionMap[String(searchInfo.profession)] || String(searchInfo.profession);
          this.profession = this.professions.find(o => o.value === pCode) || null;
        } else {
          this.profession = null;
        }

        if (savedRisks && typeof savedRisks === 'object') {
          const parsed = {};
          Object.keys(savedRisks).forEach(k => {
            const v = savedRisks[k];
            if (typeof v === 'boolean') {
              parsed[k] = v;
            } else if (typeof v === 'string') {
              parsed[k] = (v === 'Y' || v === '1' || v.toLowerCase() === 'true');
            }
          });
          this.risks = { ...this.risks, ...parsed };
          this.selectedPackage = isSavedChild ? 'child' : (searchInfo.selectedPackage || detectPackageFromRisks(this.risks, false));
        } else {
          this.selectedPackage = isSavedChild ? 'child' : 'adults';
          this.risks = getDefaultAccidentRisks(this.selectedPackage, isSavedChild);
        }
        this.risks.RiskDeathAccident = true;
      }
    }
  },
  methods: {
    toggleSportsList() {
      this.showSportsList = !this.showSportsList;
    },
    isRiskActive(key) {
      if (!this.risks) return key === 'RiskDeathAccident';
      if (key === 'RiskDeathAccident') return true;

      if (key === 'RiskGroupOneTwoAccident') {
        const r1 = this.risks.RiskGroupOneАccident ?? this.risks.RiskGroupOneAccident;
        const r2 = this.risks.RiskGroupTwoАccident ?? this.risks.RiskGroupTwoAccident;
        return (r1 === true || r1 === 'Y' || r1 === '1' || r1 === 1) &&
               (r2 === true || r2 === 'Y' || r2 === '1' || r2 === 1);
      }

      if (key === 'RiskGroupOneTwoIllness') {
        const r1 = this.risks.RiskGroupOneIllness;
        const r2 = this.risks.RiskGroupTwoIllness;
        return (r1 === true || r1 === 'Y' || r1 === '1' || r1 === 1) &&
               (r2 === true || r2 === 'Y' || r2 === '1' || r2 === 1);
      }

      const v = this.risks[key];
      return v === true || v === 'Y' || v === '1' || v === 1;
    },
    toggleRisk(key, isMandatory) {
      if (isMandatory || key === 'RiskDeathAccident') return;

      if (key === 'RiskGroupOneTwoAccident') {
        const next = !this.isRiskActive('RiskGroupOneTwoAccident');
        this.$set(this.risks, 'RiskGroupOneАccident', next);
        this.$set(this.risks, 'RiskGroupOneAccident', next);
        this.$set(this.risks, 'RiskGroupTwoАccident', next);
        this.$set(this.risks, 'RiskGroupTwoAccident', next);
        return;
      }

      if (key === 'RiskGroupOneTwoIllness') {
        const next = !this.isRiskActive('RiskGroupOneTwoIllness');
        this.$set(this.risks, 'RiskGroupOneIllness', next);
        this.$set(this.risks, 'RiskGroupTwoIllness', next);
        return;
      }

      if (key === 'RiskGroupThreeАccident' || key === 'RiskGroupThreeAccident') {
        const next = !this.isRiskActive('RiskGroupThreeАccident');
        this.$set(this.risks, 'RiskGroupThreeАccident', next);
        this.$set(this.risks, 'RiskGroupThreeAccident', next);
        return;
      }

      if (key === 'RiskGroupThreeIllness') {
        const next = !this.isRiskActive('RiskGroupThreeIllness');
        this.$set(this.risks, 'RiskGroupThreeIllness', next);
        return;
      }

      const current = this.isRiskActive(key);
      const next = !current;
      this.$set(this.risks, key, next);

      if (key === 'TemporaryDisability03P' && next) {
        this.$set(this.risks, 'TemporaryDisability01P', false);
      } else if (key === 'TemporaryDisability01P' && next) {
        this.$set(this.risks, 'TemporaryDisability03P', false);
      }
    },
    selectPackage(pkgId) {
      this.selectedPackage = pkgId;
      const def = getDefaultAccidentRisks(pkgId, this.isChild);
      Object.keys(def).forEach(k => {
        this.$set(this.risks, k, def[k]);
      });
      this.risks.RiskDeathAccident = true;
    },
    setSearchParams() {
      this.submitCheck = true;

      if (this.$v.$invalid) {
        this.$v.$touch()
        return
      }

      const activeFactors = [];
      if (this.hasWarRisk) activeFactors.push('warRisk');
      if (this.hasSport) activeFactors.push('sport');
      if (!this.isChild && this.hasProfession) activeFactors.push('profession');

      let resolvedSportType = null;
      let resolvedSportCategory = null;
      let resolvedSportName = null;
      let resolvedSportGroup = null;

      if (this.hasSport) {
        resolvedSportCategory = this.sportCategory ? this.sportCategory.value : 'amateur';
        if (this.isSportGroup && this.sportGroup) {
          resolvedSportGroup = this.sportGroup.code;
          resolvedSportType = resolveSportGroupCode(this.sportGroup.code, resolvedSportCategory);
          resolvedSportName = this.sportGroup.name;
        } else if (this.sportType) {
          resolvedSportType = this.sportType.value;
          resolvedSportName = this.sportType.name;
        }
      }

      const risksPayload = buildAccidentRisksPayload(this.risks, this.isChild);

      const params = {
        startDate: this.start_date,
        period: this.periodOption ? this.periodOption.value : null,
        protectionPeriod: this.protectionPeriod ? this.protectionPeriod.value : null,
        birthDate: this.birthDate,
        countryId: this.country ? this.country.id : null,
        sum: this.sum ? this.sum.value : null,
        activeFactors: activeFactors,
        activeFactor: activeFactors[0] || null,
        warRisk: this.hasWarRisk,
        isSportGroup: this.hasSport ? this.isSportGroup : false,
        sportGroup: resolvedSportGroup,
        sportType: resolvedSportType,
        sportCategory: resolvedSportCategory,
        sportName: resolvedSportName,
        profession: (!this.isChild && this.hasProfession && this.profession) ? this.profession.value : null,
        professionName: (!this.isChild && this.hasProfession && this.profession) ? this.profession.name : null,
        selectedPackage: this.isChild ? 'child' : this.selectedPackage,
        accidentRisks: risksPayload,
        ...risksPayload,
      };

      this.$store.commit('CLEAR_ACCIDENT_FULL_ORDER');
      this.$store.commit('CLEAR_ACCIDENT_INFO_OFFER');
      this.$store.commit('CLEAR_ACCIDENT_CONTRACT_ID');
      this.$store.commit('CLEAR_ACCIDENT_INFO_USER');
      this.$store.commit('SET_SEARCH_ACCIDENT_PARAMS', params);

      if (this.$router.currentRoute.name === 'accidentList') {
        this.$emit('submitSearch', params);
      } else {
        this.$router.push({name: 'accidentList'});
      }
    },
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.accident-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 20px;
}

.accident-card__title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
}

.accident-factors-checkboxes {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.accident-check-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  padding: 8px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.2s;
  user-select: none;
}

.accident-check-label:hover {
  border-color: #ffad3d;
}

.accident-check-label.active {
  border-color: #ff6b35;
  color: #ff6b35;
  background: rgba(255, 107, 53, 0.05);
  font-weight: 500;
}

.accident-check-input {
  display: none;
}

.accident-check-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 10px;
  color: #fff;
  transition: all 0.2s;
}

.accident-check-label.active .accident-check-box {
  border-color: #ff6b35;
  background: #ff6b35;
}

.accident-factor-block {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
}

.accident-factor-block__title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}

.sport-mode-toggle {
  display: flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 4px;
}

.sport-mode-option {
  flex: 1;
  text-align: center;
  padding: 6px 12px;
  margin-bottom: 0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.sport-mode-option.active {
  background: #fff;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.sport-mode-radio {
  display: none;
}

.sport-group-details {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.sport-group-sports-scroll {
  max-height: 180px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 10px;
}

.sport-list-item {
  border-bottom: 1px solid #f1f5f9;
}

.sport-list-item:last-child {
  border-bottom: none;
}

.accident-submit-btn {
  min-height: 46px;
  max-width: 400px;
  width: 100%;
  border-radius: 8px;
  font-size: 15px;
}

.form-title {
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
  display: block;
}

.form-group {
  margin-bottom: 16px;
}

.accident-package-card {
  background: #ffffff;
  border: 1px solid #eef2f6;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.accident-package-card__title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 18px;
  letter-spacing: -0.2px;
}

.accident-child-program-badge {
  display: flex;
  align-items: center;
  background: #f0f7ff;
  border: 1px solid #cce3fe;
  border-radius: 12px;
  padding: 12px 16px;
  gap: 14px;
}

.accident-child-program-badge__icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 10px;
  background: #e0effe;
  color: #1a73e8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.accident-child-program-badge__title {
  font-size: 15px;
  font-weight: 700;
  color: #0d47a1;
  line-height: 1.25;
}

.accident-child-program-badge__desc {
  font-size: 12.5px;
  color: #4b6b94;
  margin-top: 2px;
  line-height: 1.3;
}

.accident-package-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
  background: #edf2f7;
  border-radius: 12px;
  padding: 4px;
  width: 100%;
  box-sizing: border-box;
}

.accident-package-tab {
  width: 100%;
  min-width: 0;
  text-align: center;
  padding: 10px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  border: none;
  background: transparent;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.18s ease-in-out;
  outline: none;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.accident-package-tab:hover:not(.active) {
  color: #1e293b;
}

.accident-package-tab.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
}

@media (max-width: 576px) {
  .accident-package-card {
    padding: 16px 14px;
  }
  .accident-package-tabs {
    padding: 3px;
    gap: 2px;
  }
  .accident-package-tab {
    padding: 8px 4px;
    font-size: 12.5px;
  }
}

.accident-package-counter {
  font-size: 13.5px;
  font-weight: 600;
  color: #475569;
  padding: 8px 4px 4px 4px;
}

.accident-package-risks-list {
  display: flex;
  flex-direction: column;
}

.accident-package-risk-row {
  display: flex;
  align-items: center;
  padding: 14px 4px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

.accident-package-risk-row:last-child {
  border-bottom: none;
}

.accident-package-risk-row.is-mandatory {
  cursor: default;
}

.accident-package-checkbox {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  border: 1.5px solid #cbd5e1;
  background: #ffffff;
  color: #ffffff;
  transition: all 0.15s ease;
}

.accident-package-checkbox.is-checked {
  background: #5468ff;
  border-color: #5468ff;
}

.accident-package-checkbox.is-locked {
  background: #9aa8bc;
  border-color: #9aa8bc;
  cursor: default;
}

.accident-package-risk-body {
  flex: 1;
}

.accident-package-risk-name {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
}

.accident-package-risk-desc {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 3px;
  line-height: 1.25;
}

.accident-package-risk-lock {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 8px;
}

.accident-extra-risks-section {
  border-top: 1px solid #eef2f6;
}

.btn-toggle-extra-risks {
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 8px 4px;
  color: #5468ff;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: opacity 0.15s ease;
}

.btn-toggle-extra-risks:hover {
  opacity: 0.85;
}

.accident-extra-risks-list {
  background: #f8fafc;
  border: 1px solid #eef2f6;
  border-radius: 12px;
  padding: 4px 12px;
}

.accident-extra-risks-list .accident-package-risk-row:last-child {
  border-bottom: none;
}

.accident-calc-grid {
  display: flex;
  flex-direction: column;
}

.accident-grid-form,
.accident-grid-factors,
.accident-grid-risks,
.accident-grid-submit {
  min-width: 0;
  width: 100%;
}

@media (min-width: 992px) {
  .accident-calc-grid {
    display: grid;
    grid-template-columns: minmax(0, 8fr) minmax(0, 4fr);
    grid-template-areas:
      "form factors"
      "risks factors"
      "submit factors";
    column-gap: 30px;
    align-items: start;
  }

  .accident-grid-form {
    grid-area: form;
  }

  .accident-grid-factors {
    grid-area: factors;
    align-self: start;
  }

  .accident-grid-risks {
    grid-area: risks;
  }

  .accident-grid-submit {
    grid-area: submit;
  }
}
</style>
